<template>
    <common-modal v-if="isOpen" width="medium" @close="emitEvent({ name: 'close' })">
        <common-content-wrapper width="medium">
            <common-content-spacer height="medium" class="referral-notes__container" :isCenter="true">
                <span>
                    <h4 v-if="patientFullName">{{ $t('modals.upload_note_modal.patient', { name: patientFullName }) }}</h4>
                    <p v-if="referral.diagnosis">
                        {{ $t('modals.upload_note_modal.diagnosis', { diagnosis: referral.diagnosis }) }}
                    </p>
                </span>
                <span>
                    <h2>{{ heading }}</h2>
                    <common-warning v-if="showWarning" size="medium" :text="warningText"></common-warning>
                    <p v-if="showLimits">{{ $t('pages.referrals.notes.add.file_limits') }}</p>
                </span>

                <input
                    ref="fileInput"
                    type="file"
                    multiple="multiple"
                    style="display: none"
                    accept="application/pdf,image/png,image/jpg,image/jpeg"
                    @change="fileSelected($event)"
                />

                <div class="referral-notes__upload-section">
                    <button
                        v-if="!selectedFiles || !selectedFiles.length"
                        class="referral-notes__upload-btn referral-notes__upload-btn--small"
                        @click="$refs.fileInput.click()"
                    >
                        <common-icon name="cloud" size="gigantic"></common-icon>
                        <span>{{ $t('modals.upload_note_modal.buttons.add_files') }}</span>
                    </button>

                    <common-upload-summary
                        v-else
                        :isUploading="isUploading"
                        :files="selectedFiles"
                        @remove-file="removeFile($event)"
                    ></common-upload-summary>
                </div>

                <common-buttons class="referral-notes__buttons">
                    <common-button
                        :label="loadMoreLabel"
                        type="secondary"
                        size="large"
                        :disabled="!selectedFiles.length"
                        :isProcessing="isUploading"
                        @click="$refs.fileInput.click()"
                    ></common-button>
                    <common-button
                        v-if="!showConfirm"
                        :label="$t('modals.upload_note_modal.buttons.upload_files')"
                        type="primary"
                        size="large"
                        :disabled="!selectedFiles.length || isSubmitDisabled"
                        :isProcessing="isUploading"
                        @click="sendFiles"
                    ></common-button>
                    <common-button
                        v-else
                        :label="$t('modals.upload_note_modal.buttons.confirm')"
                        type="primary"
                        size="large"
                        :disabled="!selectedFiles.length || isSubmitDisabled"
                        :isProcessing="isUploading"
                        @click="confirm"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
        </common-content-wrapper>
    </common-modal>
</template>

<script>
    import ReferralsAPI from 'api/ReferralsAPI'
    import UploadMixin from 'mixins/UploadMixin'

    export default {
        mixins: [UploadMixin],

        props: {
            isOpen: { type: Boolean, required: true },
            referral: { type: Object, required: true },
        },

        computed: {
            patientFullName() {
                const patient = this.referral.patient
                if (!patient || !patient.firstName || !patient.lastName) return null
                return `${this.referral.patient.firstName} ${this.referral.patient.lastName}`
            },

            loadMoreLabel() {
                const fileNotUploaded = this.selectedFiles.find((file) => !file.isUploaded)
                if (!this.selectedFiles.length || !!fileNotUploaded) return this.$t('modals.upload_note_modal.buttons.load_more')

                return this.$t('modals.upload_note_modal.buttons.upload_more')
            },

            showConfirm() {
                const fileNotUploaded = this.selectedFiles.find((file) => !file.isUploaded)
                return this.selectedFiles.length && !fileNotUploaded
            },

            heading() {
                if (this.isUploading) {
                    return this.$t('pages.referrals.notes.add.heading_uploading')
                } else if (this.uploadedFilesCount) {
                    return this.$t('pages.referrals.notes.add.heading_uploaded', { n: this.uploadedFilesCount })
                }
                return this.$t('modals.upload_note_modal.heading')
            },
        },

        methods: {
            sendFiles() {
                this.isUploading = true
                this.showWarning = false
                this.showLimits = true
                Promise.all(
                    this.selectedFiles.map((file) => {
                        if (!file.isUploaded) return this.convertFileToBase64(file)
                        return Promise.resolve(file)
                    }),
                ).then((files) => {
                    this.selectedFiles = files
                    const filesForUpload = files.filter((file) => !file.isUploaded)
                    this.uploadFiles(filesForUpload)
                })
            },

            uploadFiles(files) {
                ReferralsAPI.uploadConsultationNotes(files).then(() => {
                    this.isUploading = false
                    if (this.errorFilesCount) {
                        this.warningText = this.$t('pages.referrals.notes.add.warnings.error', { n: this.errorFilesCount })
                        this.showWarning = true
                        this.showLimits = false
                        return
                    }
                })
            },

            removeFile(event) {
                if (!this.isUploading) {
                    this.removeAndValidateFiles(event)
                }
            },

            fileSelected(event) {
                this.onFileSelected(event)
                this.$refs.fileInput.value = ''
            },

            confirm() {
                this.emitEvent({ name: 'files-uploaded', payload: this.selectedFiles })
            },
        },
    }
</script>
